import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Search, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getMembers } from '../api/members';
import AddMemberModal from '../components/members/AddMemberModal';

const AVBG = ['var(--accent-bg)','var(--success-bg)','var(--warning-bg)','#1a0d2e','#0a1f2e'];
const AVCO = ['var(--accent)','var(--success)','var(--warning)','#a78bfa','#38bdf8'];

export default function MembersPage() {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['members', q, page],
    queryFn: () => getMembers({ q, page, limit: 25 }),
  } as any);

  const members = (data as any)?.data?.members || [];
  const total = (data as any)?.data?.total || 0;
  const totalPages = Math.ceil(total / 25);

  return (
    <>
      <AddMemberModal open={showAdd} onClose={() => setShowAdd(false)} />
      <div style={{padding:24,fontFamily:'system-ui,sans-serif'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
          <div>
            <h1 style={{fontSize:20,fontWeight:600,color:'var(--text-primary)',margin:0}}>{t('members')}</h1>
            <p style={{fontSize:12,color:'var(--text-faint)',marginTop:4}}>{total.toLocaleString()} {t('members')}</p>
          </div>
          <button onClick={() => setShowAdd(true)}
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer'}}>
            <UserPlus size={15} /> {t('add_member')}
          </button>
        </div>

        <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #1e2d45'}}>
            <div style={{position:'relative',maxWidth:360}}>
              <Search size={14} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-dimmed)'}} />
              <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
                placeholder={t('search_members')}
                style={{width:'100%',background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8,padding:'8px 12px 8px 32px',fontSize:12,color:'var(--text-secondary)',outline:'none'}} />
            </div>
          </div>

          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{borderBottom:'1px solid #1e2d45'}}>
                {[t('name'),t('email'),t('type'),t('barcode'),t('checkouts')].map(h => (
                  <th key={h} style={{textAlign:'left',padding:'10px 16px',fontSize:11,fontWeight:500,color:'var(--text-faint)',letterSpacing:'0.03em'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={5} style={{textAlign:'center',padding:32,color:'var(--text-dimmed)'}}>{t('loading')}</td></tr>}
              {members.map((m: any, i: number) => {
                const ci = i % 5;
                return (
                  <tr key={m.id}
                    style={{borderBottom: !m.is_complete ? '1px solid #5a1a1a' : '1px solid #0f1826', cursor:'pointer', background: !m.is_complete ? '#1a0a0a' : 'transparent'}}
                    onClick={() => navigate(`/members/${m.id}`)}
                    onMouseEnter={e => (e.currentTarget.style.background='var(--bg-hover)')}
                    onMouseLeave={e => (e.currentTarget.style.background='transparent')}>
                    <td style={{padding:'10px 16px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div style={{width:30,height:30,borderRadius:8,background:AVBG[ci],border:`1px solid ${AVCO[ci]}33`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:600,color:AVCO[ci],flexShrink:0}}>
                          {(m.first_name?.[0]||'?').toUpperCase()}
                        </div>
                        <span style={{color:'var(--text-secondary)'}}>{m.last_name} {m.first_name}</span>
                      </div>
                    </td>
                    <td style={{padding:'10px 16px',color:'var(--text-faint)',fontSize:11}}>{m.email||'—'}</td>
                    <td style={{padding:'10px 16px'}}>
                      {m.member_type
                        ? <span style={{display:'inline-flex',padding:'2px 8px',borderRadius:6,fontSize:11,background:'var(--bg-dark2)',border:'1px solid #1e2d45',color:'var(--text-muted)'}}>{m.member_type}</span>
                        : <span style={{color:'var(--text-dimmed)',fontSize:11}}>—</span>}
                    </td>
                    <td style={{padding:'10px 16px',color:'var(--text-dimmed)',fontSize:11,fontFamily:'monospace'}}>{m.barcode||'—'}</td>
                    <td style={{padding:'10px 16px'}}>
                      {m.active_checkouts>0
                        ? <span style={{display:'inline-flex',padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:500,background:'var(--warning-bg)',color:'var(--warning)'}}>{m.active_checkouts}</span>
                        : <span style={{color:'var(--text-dimmed)',fontSize:11}}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{padding:'12px 16px',borderTop:'1px solid #1e2d45',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span style={{fontSize:11,color:'var(--text-dimmed)'}}>{t('page')} {page} {t('of')} {totalPages}</span>
            <div style={{display:'flex',gap:6}}>
              <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
                style={{padding:'5px 12px',background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:6,fontSize:11,color:page===1?'var(--border)':'var(--text-muted)',cursor:page===1?'not-allowed':'pointer'}}>{t('prev')}</button>
              <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page>=totalPages}
                style={{padding:'5px 12px',background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:6,fontSize:11,color:page>=totalPages?'var(--border)':'var(--text-muted)',cursor:page>=totalPages?'not-allowed':'pointer'}}>{t('next')}</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
